import type { HTMLAttributes, ReactElement, SVGAttributes } from "react";
import type { CancelablePromise } from "cancelable-promise";
import type * as d3Zoom from "d3-zoom";
import type {
  ApiEntity,
  AtlasValues,
  AtlasViewBoxRequest,
  BaseRequest,
  Boundaries,
  CentroidResponse,
  IdKeyType,
  LabelsOptions,
  MapViewBoxRequest,
  ProjectionsList,
  Properties,
  RequestOptions,
  RequestPromise,
  SearchOptions,
  Size,
  StylesOptions,
  ViewBoxProps,
} from "@hokuto/jam-core";
import { CanvasDrawer } from "./utils/drawer.js";
import { Observable } from "./utils/observable.js";
import { getTilesProps } from "./utils/tiles.js";
import { useCanvasParams } from "./hooks/params.js";
import { ParamsObserver, ZoomControls } from "./utils/index.js";

export enum ApiMethod {
  Get = "GET",
  Post = "POST",
  Put = "PUT",
  Delete = "DELETE",
}

export type BaseParams<T extends BaseRequest> = T &
  Size & {
    scale: number;
    margin: Boundaries;
    body?: Partial<RequestOptions>;
  };

export type JamMapParams = BaseParams<MapViewBoxRequest>;
export type JamAtlasParams = BaseParams<AtlasViewBoxRequest>;

export type JamParams = JamMapParams | JamAtlasParams;

export interface TilesProps extends Size {
  margin: Boundaries;
  tileSize?: number;
  tileIncrement?: number;
}

export type ZoomType<T extends Element> = ReturnType<
  typeof d3Zoom.zoom<T, unknown>
>;

export type ZoomControlsObservable = Observable<{
  current?: d3Zoom.ZoomTransform;
  previous?: d3Zoom.ZoomTransform;
  action?: "zoom" | "start" | "end";
  sourceEvent?: MouseEvent;
}>;

export interface ZoomControlsProps<T extends Element> {
  element: T;
  zoom: ZoomType<T>;
  initialSize: Size;
  observable: ZoomControlsObservable;
  duration?: number;
}

export interface ZoomControlsSchema<T extends Element> {
  zoomIn: (args?: { value?: number; duration?: number }) => void;
  zoomOut: (args?: { value?: number; duration?: number }) => void;
  panLeft: (args?: { value?: number; duration?: number }) => void;
  panRight: (args?: { value?: number; duration?: number }) => void;
  panTop: (args?: { value?: number; duration?: number }) => void;
  panBottom: (args?: { value?: number; duration?: number }) => void;
  transform: (args: { value: d3Zoom.ZoomTransform; duration?: number }) => void;
  center: (args: { width: number; height: number; duration?: number }) => void;
  reset: (duration?: number) => void;
  transformation: () => d3Zoom.ZoomTransform | undefined;
  element: T;
  zoom: ZoomType<T>;
  initialSize: Size;
  observable: ZoomControlsObservable;
}

export interface JamParamsChangeProps {
  mapParams?: ReturnType<typeof useCanvasParams>["mapParams"];
  atlasParams?: ReturnType<typeof useCanvasParams>["atlasParams"];
}

export interface JamCanvasStatus
  extends Required<Omit<TilesProps, "margin" | "tileIncrement">> {
  scale: number;
  x: number;
  y: number;
  width: number;
  height: number;
  bgColor: string;
}

export type JamCanvasAnimate = boolean | number;

export interface JamTileStatus {
  w: number;
  h: number;
  margin: Boundaries;
}

export interface JamTileImageProps {
  img?: ImageBitmap | HTMLImageElement;
  scale: number;
  getImage: () => CancelablePromise<Blob>;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface JamCanvasDrawerStatus {
  count: number;
  total: number;
  alpha: number;
}

export interface ElementMouseEvent extends ViewBoxProps {
  event: MouseEvent;
}

export type ElementChangeEvent = Omit<ElementMouseEvent, "point" | "event">;

export type JamHandlerType<Params extends unknown[], Response = unknown> = {
  (...args: Params): Response;
  hasCallback?: boolean;
};

export type ElementObserverProps<
  T extends Element = Element,
  M = ElementMouseEvent,
  C = ElementChangeEvent
> = {
  element?: T;
  onChange?: (event: C) => void;
  onClick?: (event: M) => void;
  onMove?: (event: M) => void;
  onLeave?: (event: M) => void;
  waitChange?: number;
  waitClick?: number;
  waitMove?: number;
  waitLeave?: number;
};

export type JamParamsMouseEvent = ElementMouseEvent & JamParamsChangeProps;

export type JamParamsObserverProps = Omit<
  ElementObserverProps<
    HTMLCanvasElement,
    JamParamsMouseEvent,
    JamParamsChangeProps
  >,
  "element"
> &
  JamParamsChangeProps & {
    onError?: (error: Error) => void;
    onDraw?: (args: JamCanvasDrawerStatus) => void;
    controls?: ZoomControlsSchema<HTMLCanvasElement>;
  };

export type JamBaseProps = BaseRequest &
  Partial<TilesProps> & {
    [IdKeyType.Map]?: string;
    [IdKeyType.Atlas]?: string;
    [ApiEntity.Atlas]?: AtlasValues;
    projection?: ProjectionsList;
    bgColor?: string;
    minZoom?: number;
    maxZoom?: number;
    duration?: number;
    init?: d3Zoom.ZoomTransform;
    paramsObserver?: ParamsObserver;
  };

export interface JamDrawerBaseOptions extends Omit<StylesOptions, "labels"> {
  body?: Partial<RequestOptions>;
  search?: SearchOptions;
}

export interface JamDrawerBaseParams extends JamParamsChangeProps {
  canvasStatus: JamCanvasStatus;
  controls: ZoomControls<HTMLCanvasElement>;
  drawerIndex: number;
  drawersObservable: Observable<{ drawers: CanvasDrawer[] }>;
  tilesProps: ReturnType<typeof getTilesProps>;
  paramsObserver?: ParamsObserver;
}

export interface JamDrawerProps<
  T extends JamDrawerBaseOptions = JamDrawerBaseOptions
> extends JamDrawerBaseParams {
  canvasDrawer: CanvasDrawer;
  options?: T;
  showMonitor?: boolean;
  onError?: (error: Error) => void;
}

export type JamCanvasMapProps = JamDrawerProps<
  JamDrawerBaseOptions & {
    progressive?: boolean;
    animate?: JamCanvasAnimate;
    minTiles?: number;
  }
>;

export type JamCanvasLabelsProps = JamDrawerProps<
  JamDrawerBaseOptions & {
    labels?: LabelsOptions;
  }
>;

export type JamCanvasFeatureProps = JamDrawerProps<
  Omit<JamDrawerBaseOptions, "search">
> & {
  [IdKeyType.Feature]: string;
};

export type JamCanvasCentroidsProps = JamDrawerProps<
  Pick<JamDrawerBaseOptions, "search" | "bgColor">
> & {
  drawer?: (args: {
    centroids: CentroidResponse;
    canvasDrawer: CanvasDrawer;
  }) => void;
};

export type JamCanvasElement = ReactElement<Partial<JamDrawerProps>>;

export type JamCanvasProps = Omit<
  HTMLAttributes<HTMLCanvasElement>,
  "children"
> &
  JamBaseProps & {
    children: JamCanvasElement | JamCanvasElement[];
  };

export interface ResizableContainerProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children"> {
  children?: (size: Size) => JSX.Element;
}

// export type DataRequestType<T = unknown> = RequestPromise<T> | undefined;

export interface ApiValues<T> {
  dataRequest?: RequestPromise<T>;
  setDataRequest: (
    dataRequest?: RequestPromise<T>
  ) => RequestPromise<T> | undefined;
  showComponent: (cmp: JSX.Element) => JSX.Element;
  error?: Error;
  loading: boolean;
}

export interface FetchOptions extends RequestInit {
  params?: Properties;
  body?: RequestInit["body"];
}

export enum LoadingSize {
  Xs = "xs",
  Sm = "sm",
  Md = "md",
  Lg = "lg",
  Xl = "xl",
}

export interface LoadingProps extends SVGAttributes<SVGElement> {
  size?: LoadingSize;
  color?: string;
}

export interface ApiErrorCause {
  status: number;
  text: string;
}
